import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import FileSaver from "file-saver";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import "../ShopifyLiveSale/shopifyGraphql.css";
import "./productwiseSessionReport.css";

const TZ = "Asia/Kolkata";
const CHART_LINE_COLOR = "#4db8e8";
const NUM = new Intl.NumberFormat("en-IN");
const PCT = new Intl.NumberFormat("en-IN", { style: "percent", maximumFractionDigits: 2 });

const FILTER_KEYS = ["source_table"];
const FILTER_LABELS = {
	source_table: "Source",
};

const METRICS = {
	sessions: { label: "Sessions", field: "sessions", format: "number" },
	online_store_visitors: { label: "Online store visitors", field: "online_store_visitors", format: "number" },
	sessions_with_cart_additions: {
		label: "Sessions with cart additions",
		field: "sessions_with_cart_additions",
		format: "number",
	},
	sessions_that_reached_checkout: {
		label: "Sessions that reached checkout",
		field: "sessions_that_reached_checkout",
		format: "number",
	},
	bounce_rate: { label: "Bounce rate", field: "bounce_rate", format: "percent" },
	conversion_rate: { label: "Conversion rate", field: "conversion_rate", format: "percent" },
	bounces: { label: "Bounces", field: "bounces", format: "number" },
	average_session_duration: {
		label: "Avg session duration (s)",
		field: "average_session_duration",
		format: "duration",
	},
	added_to_cart_rate: { label: "Added to cart rate", field: "added_to_cart_rate", format: "percent" },
};

const DATE_LABELS = {
	today: "Today",
	yesterday: "Yesterday",
	last_7_days: "Last 7 days",
	last_30_days: "Last 30 days",
	this_month: "This month",
	last_month: "Last month",
};

const toISODate = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const defaultCustomDates = () => {
	const end = new Date();
	const start = new Date();
	start.setDate(start.getDate() - 6);
	return { start: toISODate(start), end: toISODate(end) };
};

const formatDayLabel = (value) => {
	if (!value) return "";
	const date = new Date(`${value}T12:00:00`);
	return date.toLocaleDateString("en-IN", { timeZone: TZ, month: "short", day: "numeric" });
};

const formatMetricValue = (metricKey, value) => {
	const metric = METRICS[metricKey];
	if (!metric) return value ?? "-";
	if (metric.format === "percent") return PCT.format(Number(value || 0));
	if (metric.format === "duration") return `${NUM.format(Math.round(Number(value || 0)))}s`;
	return NUM.format(Number(value || 0));
};

const compactListLabel = (values, fallback = "All") => {
	if (!values?.length) return fallback;
	if (values.length === 1) return values[0];
	return `${values[0]} +${values.length - 1}`;
};

const FilterMultiSelect = ({ label, options, selected, onApply, onClear }) => {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState(selected);
	const [search, setSearch] = useState("");
	const ref = useRef(null);

	useEffect(() => {
		setDraft(selected);
	}, [selected]);

	useEffect(() => {
		const handleClick = (event) => {
			if (ref.current && !ref.current.contains(event.target)) setOpen(false);
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, []);

	const filtered = useMemo(() => {
		const term = search.trim().toLowerCase();
		return (options || []).filter((item) => !term || String(item).toLowerCase().includes(term));
	}, [options, search]);

	const toggle = (value) => {
		setDraft((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
	};

	return (
		<div className="shopify-graphql-filter-inline" ref={ref}>
			<span className="shopify-graphql-filter-inline-label">{label}</span>
			<details className="shopify-graphql-pill-dropdown" open={open}>
				<summary
					className="shopify-graphql-pill shopify-graphql-pill-summary"
					onClick={(event) => {
						event.preventDefault();
						setOpen((prev) => !prev);
					}}>
					{compactListLabel(selected, `All ${label.toLowerCase()}`)}
				</summary>
				<div className="shopify-graphql-filter-menu shopify-graphql-filter-menu-vendor">
					<input
						type="search"
						className="shopify-graphql-filter-search"
						placeholder="Search"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
					/>
					<div className="shopify-graphql-filter-options">
						{filtered.map((value) => (
							<label key={value}>
								<input type="checkbox" checked={draft.includes(value)} onChange={() => toggle(value)} />
								<span>{value}</span>
							</label>
						))}
					</div>
					<div className="shopify-graphql-filter-actions">
						<button type="button" className="shopify-graphql-filter-clear" onClick={() => setDraft([])}>
							Clear
						</button>
						<button
							type="button"
							className="shopify-graphql-filter-apply"
							onClick={() => {
								onClear?.();
								onApply(draft);
								setOpen(false);
							}}>
							Apply
						</button>
					</div>
				</div>
			</details>
		</div>
	);
};

const buildChartOption = (rows, chartMetric) => {
	const metric = METRICS[chartMetric];
	const categories = rows.map((row) => formatDayLabel(row.day));
	const values = rows.map((row) => Number(row?.[metric.field] || 0));

	return {
		grid: { left: 56, right: 24, top: 24, bottom: 40 },
		tooltip: {
			trigger: "axis",
			className: "shopify-graphql-echarts-tooltip",
			formatter: (params) => {
				const point = params?.[0];
				if (!point) return "";
				return `<div class="shopify-graphql-tooltip-title">${point.axisValue}</div>
          <div class="shopify-graphql-tooltip-row"><span>${metric.label}</span><strong>${formatMetricValue(chartMetric, point.data)}</strong></div>`;
			},
		},
		xAxis: {
			type: "category",
			data: categories,
			boundaryGap: false,
			axisLine: { lineStyle: { color: "#d9d9d9" } },
			axisLabel: { color: "#616161" },
		},
		yAxis: {
			type: "value",
			axisLabel: {
				color: "#616161",
				formatter: (value) => {
					if (metric.format === "percent") return `${(value * 100).toFixed(0)}%`;
					if (metric.format === "duration") return `${Math.round(value)}s`;
					if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
					if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
					return value;
				},
			},
			splitLine: { lineStyle: { color: "#f0f0f0" } },
		},
		series: [
			{
				name: metric.label,
				type: "line",
				smooth: 0.45,
				showSymbol: false,
				lineStyle: { width: 1.75, color: CHART_LINE_COLOR },
				itemStyle: { color: CHART_LINE_COLOR },
				areaStyle: {
					color: {
						type: "linear",
						x: 0,
						y: 0,
						x2: 0,
						y2: 1,
						colorStops: [
							{ offset: 0, color: "rgba(77, 184, 232, 0.18)" },
							{ offset: 1, color: "rgba(77, 184, 232, 0.02)" },
						],
					},
				},
				data: values,
			},
		],
	};
};

const ProductwiseSessionReport = () => {
	const { start: defaultStart, end: defaultEnd } = defaultCustomDates();
	const [during, setDuring] = useState("last_7_days");
	const [customStart, setCustomStart] = useState(defaultStart);
	const [customEnd, setCustomEnd] = useState(defaultEnd);
	const [filters, setFilters] = useState(() =>
		FILTER_KEYS.reduce((acc, key) => {
			acc[key] = [];
			return acc;
		}, {})
	);
	const [filterOptions, setFilterOptions] = useState(() =>
		FILTER_KEYS.reduce((acc, key) => {
			acc[key] = [];
			return acc;
		}, {})
	);
	const [chartMetric, setChartMetric] = useState("sessions");
	const [rows, setRows] = useState([]);
	const [summary, setSummary] = useState({});
	const [meta, setMeta] = useState({});
	const [loading, setLoading] = useState(true);
	const [filtersLoading, setFiltersLoading] = useState(false);
	const [error, setError] = useState("");
	const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
	const [tableSort, setTableSort] = useState({ key: "day", direction: "asc" });
	const [downloading, setDownloading] = useState(false);
	const requestSeqRef = useRef(0);

	const todayIso = toISODate(new Date());

	const activeDateLabel = useMemo(() => {
		if (during === "custom" && customStart && customEnd) {
			return `${formatDayLabel(customStart)} - ${formatDayLabel(customEnd)}`;
		}
		return DATE_LABELS[during] || during;
	}, [during, customStart, customEnd]);

	const sortedTableRows = useMemo(() => {
		const sorted = [...rows];
		const { key, direction } = tableSort;
		sorted.sort((a, b) => {
			const left = a?.[key];
			const right = b?.[key];
			if (key === "day") return direction === "asc" ? String(left).localeCompare(String(right)) : String(right).localeCompare(String(left));
			const diff = Number(left || 0) - Number(right || 0);
			return direction === "asc" ? diff : -diff;
		});
		return sorted;
	}, [rows, tableSort]);

	const chartOption = useMemo(() => buildChartOption(rows, chartMetric), [rows, chartMetric]);

	const loadFilterOptions = useCallback(async () => {
		setFiltersLoading(true);
		try {
			const response = await pythonVmInstance.get("/reports/productwise-session/filters");
			const payload = response?.data || {};
			const nextOptions = FILTER_KEYS.reduce((acc, key) => {
				acc[key] = (payload?.filters?.[key] || []).map((item) => item?.key || item?.name).filter(Boolean);
				return acc;
			}, {});
			setFilterOptions(nextOptions);
		} catch (requestError) {
			setError(requestError?.response?.data?.error || "Unable to load filter options.");
		} finally {
			setFiltersLoading(false);
		}
	}, []);

	const loadReport = useCallback(async () => {
		const requestId = ++requestSeqRef.current;
		setLoading(true);
		setError("");
		try {
			const params = {
				during,
				...FILTER_KEYS.reduce((acc, key) => {
					acc[key] = JSON.stringify(filters[key] || []);
					return acc;
				}, {}),
			};
			if (during === "custom") {
				params.start = customStart;
				params.end = customEnd;
			}

			const response = await pythonVmInstance.get("/reports/productwise-session/report", { params });
			if (requestId !== requestSeqRef.current) return;

			const payload = response?.data || {};
			if (!payload?.rows?.length) {
				throw new Error("No session data returned for this period.");
			}
			setRows(payload.rows);
			setSummary(payload.summary || {});
			setMeta(payload);
			setLastRefreshedAt(new Date());
		} catch (requestError) {
			if (requestId !== requestSeqRef.current) return;
			setRows([]);
			setSummary({});
			setError(requestError?.response?.data?.error || requestError?.message || "Unable to load report.");
		} finally {
			if (requestId === requestSeqRef.current) setLoading(false);
		}
	}, [during, customStart, customEnd, filters]);

	useEffect(() => {
		loadFilterOptions();
	}, [loadFilterOptions]);

	useEffect(() => {
		loadReport();
	}, [loadReport]);

	const handleDownload = async () => {
		setDownloading(true);
		try {
			const headers = ["day", ...Object.keys(METRICS).map((key) => METRICS[key].field)];
			const csvRows = [
				headers.join(","),
				...rows.map((row) =>
					headers
						.map((header) => {
							const value = row?.[header] ?? "";
							const text = String(value);
							return text.includes(",") ? `"${text}"` : text;
						})
						.join(",")
				),
			];
			const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
			FileSaver.saveAs(blob, `SESSIONS_REPORT_${new Date().toISOString().split("T")[0]}.csv`);
		} catch (requestError) {
			setError(requestError?.message || "Download failed.");
		} finally {
			setDownloading(false);
		}
	};

	const toggleSort = (key) => {
		setTableSort((prev) => ({
			key,
			direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
		}));
	};

	return (
		<div className="shopify-graphql-scroll-root">
			<div className="shopify-graphql productwise-session-report">
				<div className="shopify-graphql-container">
					<div className="shopify-graphql-page-header productwise-session-header">
						<div className="productwise-session-header-copy">
							<h1>Session Report</h1>
							<p className="productwise-session-subtitle">
								Day-on-day session analytics from Shopify combined sessions
							</p>
						</div>
					</div>

					<div className="shopify-graphql-card productwise-session-card">
						<div className="shopify-graphql-topbar">
							<div className="shopify-graphql-controls">
								<button
									type="button"
									className={`shopify-graphql-icon-button ${loading ? "is-spinning" : ""}`.trim()}
									onClick={loadReport}
									disabled={loading}
									title="Refresh">
									<RefreshRoundedIcon sx={{ fontSize: 18 }} />
								</button>
								<button
									type="button"
									className="shopify-graphql-pill shopify-graphql-pill-primary"
									onClick={handleDownload}
									disabled={downloading || loading || !rows.length}>
									<DownloadRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} />
									{downloading ? "Downloading..." : "Download CSV"}
								</button>
								<select
									className="shopify-graphql-pill shopify-graphql-select"
									value={during}
									disabled={loading}
									onChange={(event) => setDuring(event.target.value)}>
									<option value="today">Today</option>
									<option value="yesterday">Yesterday</option>
									<option value="last_7_days">Last 7 days</option>
									<option value="last_30_days">Last 30 days</option>
									<option value="this_month">This month</option>
									<option value="last_month">Last month</option>
									<option value="custom">Custom range...</option>
								</select>
								{during === "custom" && (
									<div className="shopify-graphql-custom-range shopify-graphql-custom-range-inline">
										<input
											type="date"
											value={customStart}
											max={todayIso}
											disabled={loading}
											onChange={(event) => setCustomStart(event.target.value)}
										/>
										<span className="shopify-graphql-range-separator">-</span>
										<input
											type="date"
											value={customEnd}
											max={todayIso}
											disabled={loading}
											onChange={(event) => setCustomEnd(event.target.value)}
										/>
									</div>
								)}
								<select
									className="shopify-graphql-pill shopify-graphql-select"
									value={chartMetric}
									onChange={(event) => setChartMetric(event.target.value)}>
									{Object.entries(METRICS).map(([key, metric]) => (
										<option key={key} value={key}>
											{metric.label}
										</option>
									))}
								</select>
								{FILTER_KEYS.map((key) => (
									<FilterMultiSelect
										key={key}
										label={FILTER_LABELS[key]}
										options={filterOptions[key]}
										selected={filters[key]}
										onApply={(values) => setFilters((prev) => ({ ...prev, [key]: values }))}
									/>
								))}
								{filtersLoading ? <span className="shopify-graphql-filters-loading">Filter lists loading...</span> : null}
							</div>
							<div className="shopify-graphql-meta">
								<span className="shopify-graphql-range-label">{activeDateLabel}</span>
								{loading ? (
									<span className="productwise-session-meta-loading">Updating…</span>
								) : lastRefreshedAt ? (
									<span className="shopify-graphql-range-label">
										Updated {lastRefreshedAt.toLocaleTimeString("en-IN", { timeZone: TZ })}
									</span>
								) : null}
							</div>
						</div>

						{loading && !rows.length ? (
							<div className="productwise-session-loader" role="status" aria-live="polite" aria-label="Loading session report">
								<div className="productwise-session-loader-panel">
									<span className="productwise-session-loader-ring" />
									<div className="productwise-session-loader-copy">
										<strong>Loading session data</strong>
										<span>Fetching day-on-day session analytics</span>
									</div>
								</div>
								<div className="productwise-session-skeleton" aria-hidden="true">
									<div className="productwise-session-skeleton-hero" />
									<div className="productwise-session-skeleton-chart" />
									<div className="productwise-session-skeleton-rows">
										<span />
										<span />
										<span />
										<span />
									</div>
								</div>
							</div>
						) : error && !rows.length ? (
							<div className="shopify-graphql-empty">
								<h3>Unable to load report</h3>
								<p className="shopify-graphql-error-detail">{error}</p>
								<button type="button" className="shopify-graphql-pill shopify-graphql-pill-apply" onClick={loadReport}>
									Retry
								</button>
							</div>
						) : (
							<div className={`productwise-session-body ${loading ? "is-loading" : ""}`.trim()}>
								<div className="shopify-graphql-viz">
									<div className="shopify-graphql-hero">
										<span className="shopify-graphql-hero-label">{METRICS[chartMetric].label}</span>
										<strong className="shopify-graphql-hero-value">
											{formatMetricValue(chartMetric, summary?.[METRICS[chartMetric].field])}
										</strong>
									</div>
									<div className="shopify-graphql-chart-wrap">
										<ReactECharts option={chartOption} style={{ height: 330, width: "100%" }} notMerge lazyUpdate />
									</div>
									<div className="shopify-graphql-legend">
										<span className="shopify-graphql-legend-dot" />
										{activeDateLabel}
									</div>
								</div>

								<div className="shopify-graphql-table-wrap">
									<table className="shopify-graphql-table productwise-session-table">
										<thead>
											<tr>
												<th>
													<button type="button" className="shopify-graphql-sort-btn" onClick={() => toggleSort("day")}>
														Day
													</button>
												</th>
												{Object.entries(METRICS).map(([key, metric]) => (
													<th key={key}>
														<button type="button" className="shopify-graphql-sort-btn" onClick={() => toggleSort(metric.field)}>
															{metric.label}
														</button>
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											<tr className="shopify-graphql-summary-row">
												<td>Total / Avg</td>
												{Object.entries(METRICS).map(([key, metric]) => (
													<td key={key}>{formatMetricValue(key, summary?.[metric.field])}</td>
												))}
											</tr>
											{sortedTableRows.map((row) => (
												<tr key={row.day}>
													<td>{formatDayLabel(row.day)}</td>
													{Object.entries(METRICS).map(([key, metric]) => (
														<td key={key}>{formatMetricValue(key, row?.[metric.field])}</td>
													))}
												</tr>
											))}
										</tbody>
									</table>
								</div>

								{loading ? (
									<div
										className="productwise-session-overlay"
										role="status"
										aria-live="polite"
										aria-label="Updating session report">
										<div className="productwise-session-overlay-card">
											<span className="productwise-session-loader-ring" />
											<span>Refreshing…</span>
										</div>
									</div>
								) : null}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProductwiseSessionReport;
